/**
 * SsoCallback — lands the web SSO redirect (#/auth/callback?token=…&refresh=…),
 * stores the session, then sends the user into the app. On failure it routes
 * back to the login screen with an error flag.
 */

import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { applySsoTokens, parseTokensFromSearch } from '../../services/ssoSession';

// Backend rejection code → i18n key.
export const SSO_ERROR_KEYS = {
  no_account:         'login.ssoNoAccount',
  email_unverified:   'login.ssoEmailUnverified',
  domain_not_allowed: 'login.ssoDomainNotAllowed',
  account_disabled:   'login.ssoAccountDisabled',
  pending_activation: 'login.ssoPendingActivation',
  link_conflict:      'login.ssoLinkConflict',
  sso_error:          'login.ssoError',
};

export default function SsoCallback() {
  const location = useLocation();
  const { t } = useTranslation();
  const [error, setError] = useState(null); // error code or null

  useEffect(() => {
    const { token, refresh, error: errCode } = parseTokensFromSearch(location.search);
    // Backend rejected the login (no auto-create) — show the reason, no token work.
    if (errCode) { setError(errCode); return; }
    let cancelled = false;
    (async () => {
      try {
        const ok = await applySsoTokens(token, refresh);
        if (cancelled) return;
        if (ok) { window.location.replace('/'); }
        else { setError('sso_error'); }
      } catch {
        if (!cancelled) setError('sso_error');
      }
    })();
    return () => { cancelled = true; };
  }, [location.search]);

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, bgcolor: '#f8fafc', px: 3, textAlign: 'center' }}>
      {error
        ? (
          <>
            <Typography sx={{ color: '#ef4444', fontWeight: 600, maxWidth: 420 }}>
              {t(SSO_ERROR_KEYS[error] || 'login.ssoError')}
            </Typography>
            <Typography
              component="a" href="/?logout=1"
              sx={{ color: '#256878', cursor: 'pointer', fontSize: '0.9rem' }}
            >
              {t('login.submitBtn')}
            </Typography>
          </>
        )
        : (
          <>
            <CircularProgress sx={{ color: '#3E8FA3' }} />
            <Typography sx={{ color: '#64748b' }}>{t('login.submitting')}</Typography>
          </>
        )}
    </Box>
  );
}
